import React, { useMemo } from "react";

/* ---------- Line-art furniture icons (64x64 viewBox) ---------- */
const PATHS = {
  chair: "M20 6v30M44 6v30M20 6h24M20 20h24M14 36h36M20 36v22M44 36v22",
  lamp: "M32 2v16M16 38l10-20h12l10 20zM32 38v8M26 50h12",
  wardrobe: "M12 6h40v48H12zM32 6v48M26 28v8M38 28v8M16 54v6M48 54v6",
  drawers: "M10 12h44v40H10zM10 26h44M10 39h44M28 19h8M28 32h8M28 46h8",
  table: "M8 20h48M14 20v36M50 20v36M14 38h36",
  sofa: "M10 28h44v18H10zM5 30v16M59 30v16M14 28v-8h36v8M14 46v6M50 46v6",
  hammer: "M16 8h28v12H16zM30 20v38M44 12h6",
  saw: "M6 40l40-24 12 12-40 24zM12 36l4 4M20 32l4 4M28 28l4 4M36 24l4 4",
};

export function FurnitureIcon({ name, size = 64 }) {
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/* ---------- Floating furniture line-art (background layer) ---------- */
export const SET_A = [
  { n: "chair", x: "3%", y: "10%", s: 90, r: -10, d: 0 },
  { n: "lamp", x: "88%", y: "8%", s: 80, r: 8, d: -2 },
  { n: "drawers", x: "92%", y: "62%", s: 100, r: 6, d: -4 },
  { n: "table", x: "5%", y: "70%", s: 110, r: -6, d: -6 },
];

export const SET_B = [
  { n: "wardrobe", x: "2%", y: "14%", s: 110, r: -5, d: -1 },
  { n: "sofa", x: "86%", y: "12%", s: 120, r: 5, d: -3 },
  { n: "hammer", x: "90%", y: "68%", s: 80, r: 14, d: -5 },
  { n: "saw", x: "6%", y: "74%", s: 90, r: -12, d: -7 },
];

export function FloatingFurniture({ items = SET_A }) {
  return (
    <div className="decor-layer" aria-hidden="true">
      {items.map((it) => (
        <span
          key={it.n + it.x}
          className="decor-float"
          style={{ left: it.x, top: it.y, "--rot": `${it.r}deg` }}
        >
          <span
            className="decor-float__inner"
            style={{ animationDelay: `${it.d}s` }}
          >
            <FurnitureIcon name={it.n} size={it.s} />
          </span>
        </span>
      ))}
    </div>
  );
}

/* ---------- Drifting sawdust particles (dark sections) ---------- */
export function Sawdust({ count = 24 }) {
  const dots = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        left: `${(i * 37) % 100}%`,
        size: 2 + (i % 4),
        dur: 9 + ((i * 7) % 9),
        delay: -((i * 5) % 12),
        drift: (i % 2 ? 1 : -1) * (20 + ((i * 13) % 40)),
      })),
    [count],
  );
  return (
    <div className="sawdust" aria-hidden="true">
      {dots.map((d, i) => (
        <i
          key={i}
          style={{
            left: d.left,
            width: d.size,
            height: d.size,
            animationDuration: `${d.dur}s`,
            animationDelay: `${d.delay}s`,
            "--drift": `${d.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

/* ---------- Tree-ring (annual rings) motif ---------- */
export function WoodRings({ className = "" }) {
  return (
    <svg
      className={`wood-rings ${className}`}
      viewBox="0 0 400 400"
      fill="none"
      aria-hidden="true"
      data-spin="40"
    >
      {Array.from({ length: 14 }, (_, i) => (
        <ellipse
          key={i}
          cx="200"
          cy="200"
          rx={18 + i * 13.5 + (i % 3) * 2}
          ry={16 + i * 13 - (i % 2) * 3}
          stroke="currentColor"
          strokeWidth="1"
          transform={`rotate(${i * 7} 200 200)`}
        />
      ))}
      <circle cx="200" cy="200" r="4" fill="currentColor" />
    </svg>
  );
}

/* ---------- Dovetail joint divider (draws itself on scroll) ---------- */
export function DovetailDivider({ flip = false }) {
  const d = useMemo(() => {
    let p = "M0 14";
    for (let i = 0; i < 12; i++)
      p += " h20 l6 -10 h18 l6 10 h20 l6 10 h18 l6 -10";
    return p;
  }, []);
  return (
    <div
      className={`dovetail ${flip ? "dovetail--flip" : ""}`}
      aria-hidden="true"
    >
      <svg viewBox="0 0 1200 28" preserveAspectRatio="xMidYMid slice">
        <path d={d} />
      </svg>
    </div>
  );
}

/* ---------- Swinging pendant lamp ---------- */
export function PendulumLamp() {
  return (
    <div className="pendulum" aria-hidden="true">
      <svg viewBox="0 0 120 240" width="120" height="240">
        <defs>
          <radialGradient id="lampGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffd88a" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#ffd88a" stopOpacity="0" />
          </radialGradient>
        </defs>
        <g className="pendulum__swing">
          <line
            x1="60"
            y1="0"
            x2="60"
            y2="104"
            stroke="#c89b3c"
            strokeWidth="1.5"
          />
          <circle
            className="pendulum__glow"
            cx="60"
            cy="150"
            r="58"
            fill="url(#lampGlow)"
          />
          <path
            d="M34 142 Q60 92 86 142 Z"
            fill="#2b2118"
            stroke="#c89b3c"
            strokeWidth="1.5"
          />
          <circle cx="60" cy="146" r="6" fill="#ffe6ab" />
        </g>
      </svg>
    </div>
  );
}
